/**
 * Created by katarinababic on 27.6.24.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { WebinarEntry } from '../../../Model/Explore/WebinarEntry';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRequestNotificationPermission } from '../../../Hooks/useRequestNotificationPermission';
import { SubscribedEventEntry, SubscribedEventEntryApi } from '../../../Model/Coach/Schedule/SubscribedEventEntry';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { ExploreContentViewWebinarMainButtons } from './ExploreContentViewWebinarMainButtons';
import { ExploreContentViewWebinarSubscribeButtons } from './ExploreContentViewWebinarSubscribeButtons';
import { useLanguage } from '../../../Hooks/useLanguage';

const Header = styled.p`
  font-weight: 600;
`;

export type ExploreContentViewWebinarReminderProps = {
  entry: WebinarEntry;
};

export const ExploreContentViewWebinarReminder: React.FC<ExploreContentViewWebinarReminderProps> = observer(
  ({ entry }) => {
    const { requestPermission } = useRequestNotificationPermission();
    const { t } = useTrans();
    const { language: userLanguage } = useLanguage();

    const [fetching, setFetching] = useState(false);
    const [existingSubscription, setExistingSubscription] = useState<SubscribedEventEntry | undefined>();

    const hasVideoInUserLanguage = (entry.videos ?? []).some(
      (v) => v.language?.toLowerCase() === userLanguage.toLowerCase(),
    );
    const canJoinMeeting = hasVideoInUserLanguage || !dayjs().isBefore(entry.nextDate);

    useEffect(() => {
      setFetching(true);
      SubscribedEventEntryApi.findExisting({ objectId: entry.id })
        .then((res) => setExistingSubscription(res))
        .finally(() => setFetching(false));
    }, [entry]);

    useEffect(() => {
      if (existingSubscription?.earliestInterval?.durationBefore) {
        requestPermission();
      }
    }, [existingSubscription?.earliestInterval?.durationBefore, requestPermission]);

    const handleSubscribed = React.useCallback((event) => setExistingSubscription(event), []);

    const handleUnregistered = React.useCallback(() => setExistingSubscription(undefined), []);

    if (!canJoinMeeting) {
      return (
        <React.Fragment>
          <Header>{t('exploreEntryViewScreen.remindMeTitle', { defaultValue: 'Remind me' })}</Header>

          {existingSubscription ? (
            <ExploreContentViewWebinarMainButtons
              entry={entry}
              subscription={existingSubscription}
              onUnregistered={handleUnregistered}
              canJoinMeeting={canJoinMeeting}
            />
          ) : (
            <React.Fragment>
              {!fetching && (
                <ExploreContentViewWebinarSubscribeButtons
                  entry={entry}
                  onSubscribed={handleSubscribed}
                  canJoinMeeting={canJoinMeeting}
                />
              )}
            </React.Fragment>
          )}
        </React.Fragment>
      );
    }

    return null;
  },
);
